import React, { useContext, useEffect, useState } from "react";
import { get } from "../../logics/src/utils/helpers";
import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { PaymentOptionsWrapper, PaymentMethodWrapper } from "./assets/style";
import { useTheme } from "emotion-theming";
import { doPaymentHandler } from "layouts/Payment/logics/doPaymentHandler";
import { ErrorModalContext } from "wrapper/ErrorModalWrapper/context";
import { ApplicationContext } from "wrapper/ApplicationWrapper/context";
import { UserContext } from "wrapper/UserWrapper/context";
import { VerifyOtpContext } from "wrapper/VerifyOtpWrapper/context";
import {
  trackPaymentFailureBottomSheetLoad,
  trackPayNowClick,
} from "layouts/Payment/common/dependencies/tracking";
import { useHistory } from "react-router-dom";
import { getEndIconForPaymentFailure, getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { allTrackingHandlerForApps } from "helpers/utils/trackingHandler";
import { ACCORDIAN, BUTTON_TYPE, PAGE_NAMES, TRACKING_EVENTS } from "constants/index";
import { PAYMENT_METHODS } from "constants/payment";
import { checkIsConvenienceChargesExperimentEnabled, getPlatform } from "../../helpers";
import { FilledButton } from "@nykaa/payment-ui/src/Button";
import FailureDetails from "./components/FailureDetails";
import UpiFailureWrapper from "./components/UpiFailureMethodWrapper";
import CashOnDeliveryWrapper from "layouts/Payment/PaymentMethods/CashOnDeliveryWrapper";
import { CustomButtonLayout } from "./assets/style";
import { getStringFromId } from "helpers/vernacular";
import { MorePaymentMethod } from "layouts/CheckoutPayment/mobile/AllSavedPaymentMethod/MorePaymentMethod";
import { getUpiIntentListForFailure } from "layouts/PaymentAlert/helpers";
import { RetryComponent } from "./components/PaymentFailureRetryComponent";
import { IPaymentFailureConfig, IPaymentConfig, IFinalConfig } from "./@types";
import { checkAllUpiIntentVisibilityOnWeb } from "../../layouts/CheckoutPayment/helpers";
import { ExperimentAndSwitchContext } from "wrapper/ExperimentAndSwitchWrapper/context";
import { offerReversal } from "store/payment/services";
import { formatNumber } from "helpers/utils/numberFormatter";

/**
 * Component to render BottomSheet messages,
 * fallback payment options and checks whether gpayIntent will in fallback modes or not,
 * action buttons(like Pay or Retry Payments again)
 * this component also calls doPaymentHandler along with requested params
 */
export const PaymentFailure = ({
  timerCallback,
  isGPayIntentAvailable,
  fallbackPaymentOptions,
  fallbackMessages,
  setShowFallbackModeSheet,
  nonFallbackMessages,
  onRetryButtonClick,
  retryButtonText,
  handleSuccessRedirection,
}: IPaymentFailureConfig) => {
  const history = useHistory();
  const theme: Theme = useTheme();
  const { userData } = useContext(UserContext);
  const paymentContext = useContext(PaymentContext);
  const { applicationData } = useContext(ApplicationContext);
  const errorModalContext = useContext(ErrorModalContext);
  const { UpiIntentMweb, OfferReversal } = useContext(ExperimentAndSwitchContext);
  const {
    setIsVerifyOtpVisible,
    isOtpVerificationRequired,
    handleCallBackAfterVerification,
  } = useContext(VerifyOtpContext);

  const walletConfig = get(paymentContext, "walletConfig");
  const giftCardConfig = get(paymentContext, "giftCardInfoConfig");
  const paymentInfo = get(paymentContext, "paymentInfo");
  const paymentResponseV2Data = get(paymentContext, "paymentResponseV2Data");
  const prevOrderId = get(paymentResponseV2Data, "orderId");
  const previousPaymentMode = get(paymentResponseV2Data, "paymentMode");
  const masterPaymentStatus = get(paymentResponseV2Data, "masterPaymentStatus");
  const walletAmountUsed = get(paymentResponseV2Data, "walletAmountUsed");
  const cartInfo = get(paymentContext, "cartInfo");
  const initialOrderAmount = get(cartInfo, "grandTotal");
  const totalSaved = get(cartInfo, "totalSaved");
  const isCodAvailable = get(cartInfo, "isCodAvailable");
  const flow = get(paymentContext, "flow");
  const setLoading = get(paymentContext, "setLoading");
  const setIsAutoCreateOrderLoading = get(paymentContext, "setIsAutoCreateOrderLoading");
  const doPaymentConversionApi = get(paymentContext, "doPaymentConversionApi");
  const { deviceType, isApp } = applicationData;
  const { isGuest } = userData;
  const openErrorModal = get(errorModalContext, "openErrorModal");
  const orderAmount = get(paymentContext, "orderAmount");
  const offerInfo = get(paymentContext, "offersData");
  const paymentAlertInfo = get(paymentInfo, "paymentAlertInfo");
  const [allPaymentConfig, setAllPaymentConfig] = useState<IFinalConfig[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [upiIntentConfig, setUpiIntentConfig] = useState({});
  const platform = getPlatform();
  const setIsWalletCheckedGlobal = get(paymentContext, "setIsWalletChecked");
  const [isWalletChecked, setIsWalletChecked] = useState(!!walletAmountUsed);
  const showStrikedAmount = get(paymentContext, "showStrikedAmount");
  const setIsCodMethodSelected = get(paymentContext, "setIsCodMethodSelected");
  const isCodMethodSelected = get(paymentContext, "isCodMethodSelected");
  const availablePaymentMethods = cartInfo?.availablePaymentMethods;
  const cashFreePaymentGateway = cartInfo?.paymentGateway;
  let codHandlingCharge = 0;
  /**
   * updating cod charges when cod method is selected.
   */
  if (isCodMethodSelected) {
    codHandlingCharge = get(paymentContext, "codHandlingCharge") || 0;
  }
  const payNowString = getStringFromId(VERNACULAR_STRING_IDS.DYNAMIC_PAY, `Pay ₹ ${formatNumber(orderAmount + codHandlingCharge)}`, {
    count: orderAmount + codHandlingCharge,
  });


  const isAllUpiIntentAvailableOnWeb = checkAllUpiIntentVisibilityOnWeb({
    availablePaymentMethods,
    isExperimentEnable: UpiIntentMweb.experimentEnabled && UpiIntentMweb.switchEnabled,
    cashFreePaymentGateway,
  });

  const revisedPayNowText = showStrikedAmount
    ? getStringFromId(
        VERNACULAR_STRING_IDS.DYNAMIC_PAY_WITH_STRIKED_AMOUNT,
        `Pay ₹${formatNumber(orderAmount + codHandlingCharge)} &nbsp; <del> ₹${formatNumber(initialOrderAmount + codHandlingCharge)} </del>`,
        {
          oldAmount: initialOrderAmount + codHandlingCharge,
          newAmount: orderAmount + codHandlingCharge,
        }
      )
    : "";

  /**
   * @description-- function to prepare object for /paymentConversion Api call
   * @param mode - string
   */
  const getPaymentConversionParams = (mode: string) => {
    switch (mode) {
      case ACCORDIAN.COD:
        return {
          codCharges: codHandlingCharge,
        };
      case ACCORDIAN.UPI_INTENT:
        return {
          isSaved: false,
          timerCallback: timerCallback,
          packageName: upiIntentConfig?.package,
          grandTotal: orderAmount,
          merchantKey: "gpay",
        };
      default:
        return {};
    }
  };

  // passing extra parameters from context to doPaymentFactory
  const handleDoPaymentFactory = async (method: string, params: any) => {
    setLoading(true);
    let offerReversalParam = {
      orderId: prevOrderId || "",
      mode: previousPaymentMode,
      deviceType,
    };

    let offerReversalApiCall: any = () => Promise.resolve();
    if (OfferReversal.experimentEnabled) {
      offerReversalApiCall = offerReversal;
    }


    /**
     * calling offer Reversal api before redirection
     */
    offerReversalApiCall(offerReversalParam).finally(async () => {
      const requestParams: any = {
        cartInfo,
        paymentInfo,
        isGuest,
        history,
        flow,
        deviceType,
        isOtpVerificationRequired: isOtpVerificationRequired,
        isApp,
        offerInfo,
        metaDataTags: [],
        applyCartCharges: checkIsConvenienceChargesExperimentEnabled(),
      };

      const factoryCallbacks = {
        setLoading,
        setIsAutoCreateOrderLoading,
        openErrorModal,
        trackPayNowClick: trackPayNowClick,
        setIsVerifyOtpVisible,
        handleCallBackAfterVerification,
        // setShowFallbackModeSheet,
        doPaymentApi: doPaymentConversionApi,
        history,
      };

      if (params) {
        params.interactionLocation = PAGE_NAMES.PAYMENT_FAILURE_BOTTOMSHEET;
      }

      const configParams = {
        walletConfig,
        giftCardConfig,
        values: { ...params, paymentFailureFlow: true, prevOrderId },
      };
      /**
       * check user should be redirected to payment page or not
       */
      let noSuccessRedirection = await handleSuccessRedirection();
      if (noSuccessRedirection) {
        doPaymentHandler(method, configParams, requestParams, factoryCallbacks);
      }
    });
  };

  /**
   * This is to manage toggle state of saved method
   */
  const togglePaymentMethod = (method: string) => {
    /**
     * for showing updated amount in header.
     */
    setIsCodMethodSelected(method === ACCORDIAN.COD);

    setSelectedPaymentMethod(method);
  };

  /**
   * function to get the upi intent PSB config to be shown
   * by removing alert PSB's or Gpay intent for mweb if
   * isGPayIntentAvailable is true
   */
  const getUpiIntent = () => {
    if (isApp) {
      let upiIntentList = get(paymentContext, "upiIntentList");
      // getting the upiIntentList by removing UPI's those have alerting on it
      upiIntentList = getUpiIntentListForFailure(
        paymentAlertInfo,
        PAYMENT_METHODS.UPI,
        upiIntentList
      );
      if (upiIntentList?.length > 0) {
        return upiIntentList[0];
      }
    } else {
      if (isGPayIntentAvailable || isAllUpiIntentAvailableOnWeb) {
        let upiIntentList = [
          { title: "Google Pay", icon: getIcons(platform, ICONS_NAMES.GOOGLE_PAY) },
        ];
        // getting the upiIntentList by removing UPI's those have alerting on it
        upiIntentList = getUpiIntentListForFailure(
          paymentAlertInfo,
          PAYMENT_METHODS.UPI,
          upiIntentList
        );
        if (upiIntentList?.length > 0) {
          return { title: "Google Pay", icon: getIcons(platform, ICONS_NAMES.GOOGLE_PAY) };
        }
      }
    }
  };

  const commonConfig = {
    pymentMethod: "",
    isDisabled: false,
    paymentFailureFlow: true,
  };

  /**
   * this code block is  for create fallback payment options
   * configuration
   */
  useEffect(() => {
    let finalConfig: IFinalConfig[] = [];
    fallbackPaymentOptions?.length > 0 &&
      fallbackPaymentOptions.forEach((mode: string) => {
        if (mode === ACCORDIAN.COD && isCodAvailable) {
          finalConfig.push({ ...commonConfig, pymentMethod: mode });
        } else if (mode === ACCORDIAN.GPAY_INTENT) {
          let intentConfig = getUpiIntent();
          if (intentConfig && Object.keys(intentConfig)) {
            setUpiIntentConfig(intentConfig);
            finalConfig.push({ ...commonConfig, pymentMethod: mode });
          }
        }
      });
    setLoading(false);
    setAllPaymentConfig([...finalConfig]);
    setSelectedPaymentMethod(finalConfig?.[0]?.pymentMethod || "");

    /**
     * for showing updated amount in header.
     */
    if (finalConfig?.[0]?.pymentMethod === ACCORDIAN.COD) {
      setIsCodMethodSelected(true);
    }

    allTrackingHandlerForApps({
      event: TRACKING_EVENTS.FAILURE_BOTTOMSHEET_LOAD,
      isApp,
      masterPaymentStatus,
      paymentFallbackOptions: !!finalConfig?.length,
    });
    // tracking for web
    trackPaymentFailureBottomSheetLoad({
      event: TRACKING_EVENTS.FAILURE_BOTTOMSHEET_LOAD,
      masterPaymentStatus,
      paymentFallbackOptions: !!finalConfig?.length,
      cod_charges_applied: Boolean(codHandlingCharge),
      cod_charge: codHandlingCharge,
    });

    return () => {
      /**
       * on closing paymentfailure bottom sheet.
       * set isCodMethodSelected to false.
       * to update amount in header.
       */
      setIsCodMethodSelected(false);
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  /**
   * for back handling
   */
  useEffect(() => {
  let unblock: any = null;
  if (prevOrderId) {
    if (history && window.location.hash !== "#disableBack") {
      let pathName = window.location.pathname;
      let search = window.location.search;
      (history as any)?.push(pathName + search + "#disableBack");
    }


    if (window.location.hash === "#disableBack") {
      unblock = (history as any)?.block((_location: any, action: any) => {
        if (action == "POP") {
          setLoading(true);
          
          let offerReversalApiCall: any = () => Promise.resolve();
          if (OfferReversal.experimentEnabled) {
            offerReversalApiCall = offerReversal;
          }

          let offerReversalParam = {
            orderId: prevOrderId || "",
            mode: previousPaymentMode || "",
          };

          /**
           * calling offer Reversal api before redirection
           */
           offerReversalApiCall(offerReversalParam).finally(() => {
            history.go(-1);
            setLoading(false);
          });
        }
        return true;
      });
    }
  }

  return () => {
    if (history) {
      unblock && unblock();
    }
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  }, [history]); // Must be blank to work in component unmount condtion


  // function to toggle the check and uncheck state of the wallet switch
  const handleWalletToggle = () => {
    // for local state update
    setIsWalletChecked(!isWalletChecked);

    // for context state update
    setIsWalletCheckedGlobal(!isWalletChecked);
  };

  if (allPaymentConfig.length == 0) {
    return (
      <RetryComponent
        theme={theme}
        nonFallbackMessages={nonFallbackMessages}
        onRetryButtonClick={onRetryButtonClick}
        retryButtonText={retryButtonText}
        totalSaved={totalSaved}
      />
    );
  }
  return (
    <>
      {fallbackMessages && (
        <FailureDetails
          isWalletChecked={isWalletChecked}
          handleWalletToggle={handleWalletToggle}
          fallbackMessages={fallbackMessages}
          theme={theme}
          totalSaved={totalSaved}
        />
      )}
      <PaymentOptionsWrapper>
        {allPaymentConfig?.map((paymentConfig: IPaymentConfig, index: number) => {
          const isAccordianOpened = paymentConfig.pymentMethod === selectedPaymentMethod;

          const finalConfig = {
            key: index,
            ...paymentConfig,
            isAccordianOpened,
            toggleNewPaymentAccordian: () => togglePaymentMethod(paymentConfig?.pymentMethod),
            doPayment: handleDoPaymentFactory,
            endIcon: getEndIconForPaymentFailure(isAccordianOpened),
            theme,
          };
          switch (paymentConfig?.pymentMethod) {
            case ACCORDIAN.UPI_INTENT:
              return (
                <PaymentMethodWrapper key={`paymentfailure-${index}`}>
                  <UpiFailureWrapper {...finalConfig} intentConfig={upiIntentConfig} />
                </PaymentMethodWrapper>
              );
            case ACCORDIAN.COD:
              return (
                <PaymentMethodWrapper key={`paymentfailure-${index}`}>
                  <CashOnDeliveryWrapper {...finalConfig} />
                </PaymentMethodWrapper>
              );
            default:
              return <></>;
          }
        })}
        <MorePaymentMethod
          isVisible={true}
          handleSuccessRedirection={handleSuccessRedirection}
          paymentFailureFlow={true}
          setShowFallbackModeSheet={setShowFallbackModeSheet}
        />
      </PaymentOptionsWrapper>
      <CustomButtonLayout>
        <FilledButton
          text={payNowString}
          htmlText={revisedPayNowText}
          onClick={() =>
            handleDoPaymentFactory(
              selectedPaymentMethod,
              getPaymentConversionParams(selectedPaymentMethod)
            )
          }
          isDisabled={false}
          buttonType={BUTTON_TYPE.LARGE}
          dataTestId="btn_payNow"
          theme={theme}
        />
      </CustomButtonLayout>
    </>
  );
};
